// src/client/components/profile-manager/detail-view/ParsedRawData.tsx

import { Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import { useState } from 'react';

interface ParsedRawDataProps {
  rawData: string | Record<string, any> | null;
}

export default function ParsedRawData({ rawData }: ParsedRawDataProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['linkedinData']));

  if (!rawData) {
    return (
      <p className="text-sm text-gray-400 italic">No raw data available</p>
    );
  }

  let parsedData: Record<string, any> = {};
  let parseError = false;

  try {
    if (typeof rawData === 'string') {
      parsedData = JSON.parse(rawData);
    } else if (typeof rawData === 'object') {
      parsedData = rawData;
    } else {
      parseError = true;
    }
  } catch (error) {
    console.error('[ParsedRawData] Parse error:', error);
    parseError = true;
  }

  const handleCopyValue = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return String(value);
  };

  const renderNestedObject = (obj: Record<string, any>, parentKey: string) => {
    const entries = Object.entries(obj);
    
    return (
      <div className="ml-4 border-l-2 border-gray-200 pl-4 mt-2">
        <table className="w-full text-sm">
          <tbody>
            {entries.map(([key, value], index) => {
              const stringValue = renderValue(value);
              const isNestedObject = typeof value === 'object' && value !== null && !Array.isArray(value);
              
              return (
                <tr
                  key={`${parentKey}-${key}-${index}`}
                  className={index % 2 === 0 ? 'bg-white border-b' : 'bg-gray-50 border-b'}
                >
                  <td className="p-2 font-medium text-gray-600 align-top w-1/3">
                    {key}
                  </td>
                  <td className="p-2 text-gray-800 align-top">
                    {isNestedObject ? (
                      <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-100 p-2 rounded">
                        {stringValue}
                      </pre>
                    ) : (
                      <div className="break-all text-sm">
                        {stringValue}
                      </div>
                    )}
                  </td>
                  <td className="p-2 align-top w-12">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyValue(stringValue)}
                      className="h-6 w-6 p-0"
                      title="Copy value"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (parseError) {
    return (
      <div className="p-4 bg-gray-50 rounded">
        <p className="text-sm text-red-600 mb-2">Invalid data format</p>
        <pre className="text-xs text-gray-600 overflow-x-auto">
          {typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2)}
        </pre>
      </div>
    );
  }

  const entries = Object.entries(parsedData);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic">No data fields found</p>
    );
  }

  return (
    <div className="space-y-0">
      {entries.map(([key, value], index) => {
        const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
        const isExpanded = expandedSections.has(key);
        const stringValue = renderValue(value);
        
        // Special handling for linkedinData and other nested objects
        if (isObject && key === 'linkedinData') {
          return (
            <div key={key} className="border rounded-lg mb-2 bg-white">
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="font-semibold text-gray-700">{key}</span>
                  <span className="text-xs text-gray-400">
                    ({Object.keys(value).length} fields)
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyValue(stringValue);
                  }}
                  className="h-6 w-6 p-0"
                  title="Copy entire object"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </button>
              
              {isExpanded && renderNestedObject(value, key)}
            </div>
          );
        }
        
        // Regular flat rendering for non-objects
        return (
          <div
            key={key}
            className={`flex items-start py-2 px-3 border-b ${
              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
            }`}
          >
            <div className="w-1/3 font-medium text-gray-600 text-sm">
              {key}
            </div>
            <div className="flex-1 text-gray-800 text-sm">
              {isObject ? (
                <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto bg-gray-100 p-2 rounded">
                  {stringValue}
                </pre>
              ) : (
                <div className="break-all">
                  {stringValue}
                </div>
              )}
            </div>
            <div className="ml-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyValue(stringValue)}
                className="h-6 w-6 p-0"
                title="Copy value"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}